import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '../models/paginatedResponse';
import { CompanyDetailsDto } from '../models/api/companyDetailsDto';
import {
  OpbCompanyDto,
  OpbCompanyCreateRequest,
  OpbCompanyUpdateRequest,
  OpbCompaniesSearchRequest,
  OpbCompanyConvertRequest,
  OpbCompanyCsvCheckResult,
  OpbCompanyCsvImportResult,
  OpbCompanyInactivePeriodDto,
  OpbCompanyInactivePeriodCreateRequest,
  OpbCompanyInactivePeriodUpdateRequest
} from '../models/api/opbCompanyDto';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OpbCompanyService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/opb-companies`;

  /**
   * Search OPB companies with pagination and filtering
   */
  searchOpbCompanies(request: OpbCompaniesSearchRequest): Observable<PaginatedResponse<OpbCompanyDto>> {
    return this.http.post<PaginatedResponse<OpbCompanyDto>>(`${this.apiUrl}/search`, request);
  }

  /**
   * Export OPB companies to CSV based on search criteria
   */
  exportOpbCompaniesToCsv(request: OpbCompaniesSearchRequest): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/search/export`, request, { responseType: 'blob' });
  }

  /**
   * Get an OPB company by ID
   */
  getOpbCompanyById(id: string): Observable<OpbCompanyDto> {
    return this.http.get<OpbCompanyDto>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new OPB company
   */
  createOpbCompany(request: OpbCompanyCreateRequest): Observable<OpbCompanyDto> {
    return this.http.post<OpbCompanyDto>(this.apiUrl, request);
  }

  /**
   * Update an existing OPB company
   */
  updateOpbCompany(id: string, request: OpbCompanyUpdateRequest): Observable<OpbCompanyDto> {
    return this.http.put<OpbCompanyDto>(`${this.apiUrl}/${id}`, request);
  }

  /**
   * Delete an OPB company
   */
  deleteOpbCompany(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Check a CSV file for import validity without importing
   */
  checkCsv(file: File, delimiter: string = ','): Observable<OpbCompanyCsvCheckResult> {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('delimiter', delimiter);
    return this.http.post<OpbCompanyCsvCheckResult>(`${this.apiUrl}/check-csv`, formData, { params });
  }

  /**
   * Import OPB companies from a CSV file
   */
  importCsv(file: File, delimiter: string = ','): Observable<OpbCompanyCsvImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('delimiter', delimiter);
    return this.http.post<OpbCompanyCsvImportResult>(`${this.apiUrl}/import-csv`, formData, { params });
  }

  /**
   * Convert an OPB company to a Company entity
   */
  convertToCompany(id: string, request: OpbCompanyConvertRequest): Observable<CompanyDetailsDto> {
    return this.http.post<CompanyDetailsDto>(`${this.apiUrl}/${id}/convert-to-company`, request);
  }

  // --- Inactive Periods ---

  /**
   * Get all inactive periods for an OPB company
   */
  getInactivePeriods(opbCompanyId: string): Observable<OpbCompanyInactivePeriodDto[]> {
    return this.http.get<OpbCompanyInactivePeriodDto[]>(`${this.apiUrl}/${opbCompanyId}/inactive-periods`);
  }

  /**
   * Get an inactive period by ID
   */
  getInactivePeriodById(opbCompanyId: string, id: string): Observable<OpbCompanyInactivePeriodDto> {
    return this.http.get<OpbCompanyInactivePeriodDto>(`${this.apiUrl}/${opbCompanyId}/inactive-periods/${id}`);
  }

  /**
   * Create a new inactive period for an OPB company
   */
  createInactivePeriod(opbCompanyId: string, request: OpbCompanyInactivePeriodCreateRequest): Observable<OpbCompanyInactivePeriodDto> {
    return this.http.post<OpbCompanyInactivePeriodDto>(`${this.apiUrl}/${opbCompanyId}/inactive-periods`, request);
  }

  /**
   * Update an inactive period for an OPB company
   */
  updateInactivePeriod(opbCompanyId: string, id: string, request: OpbCompanyInactivePeriodUpdateRequest): Observable<OpbCompanyInactivePeriodDto> {
    return this.http.put<OpbCompanyInactivePeriodDto>(`${this.apiUrl}/${opbCompanyId}/inactive-periods/${id}`, request);
  }

  /**
   * Delete an inactive period for an OPB company
   */
  deleteInactivePeriod(opbCompanyId: string, id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${opbCompanyId}/inactive-periods/${id}`);
  }
}
